import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useConfig } from "../../context/ConfigContext";
import { getApiErrorLog, type ApiErrorEntry } from "../../utils/apiDebugLog";
import { VITE_ENV } from "../../utils/viteEnv";
import DebugDiagnostics from "./DebugDiagnostics";

function DebugSection({ title, children }: { title: string; children: unknown }) {
  return (
    <div>
      <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">{title}</p>
      <pre className="text-xs font-mono bg-white/70 border border-amber-100 rounded-lg p-3 overflow-x-auto text-stone-700 whitespace-pre-wrap break-all">
        {JSON.stringify(children, null, 2)}
      </pre>
    </div>
  );
}

export default function DebugPanel() {
  const { user, organization } = useAuth();
  const { config } = useConfig();
  const { pathname } = useLocation();
  const [apiErrors, setApiErrors] = useState<ApiErrorEntry[]>(() => getApiErrorLog());

  useEffect(() => {
    const handler = () => setApiErrors([...getApiErrorLog()]);
    window.addEventListener("api-debug-update", handler);
    return () => window.removeEventListener("api-debug-update", handler);
  }, []);

  const isAdminOrDirector = user?.role === "admin" || user?.role === "director";
  const debugEnabled = config["debug.mode"] === "true";

  if (!isAdminOrDirector || !debugEnabled) return null;

  const apiBase = VITE_ENV.API_BASE_URL;
  const version = VITE_ENV.APP_VERSION;

  return (
    <div className="p-6 lg:p-8 pt-0">
      <details className="camp-card border border-amber-200 bg-amber-50">
        <summary className="px-5 py-3 cursor-pointer text-sm font-semibold text-amber-800 select-none list-none flex items-center gap-2">
          <span className="text-amber-500">⚙</span> Debug Information
          <span className="ml-auto text-xs font-normal text-amber-600">click to expand</span>
        </summary>
        <div className="px-5 pb-5 pt-2 space-y-4 border-t border-amber-200">
          <DebugSection title="User">
            {{ id: user?.id, email: user?.email, role: user?.role, organizationId: user?.organizationId, superAdmin: user?.superAdmin }}
          </DebugSection>
          <DebugSection title="Organization">
            {{ id: organization?.id, name: organization?.name, slug: organization?.slug, planTier: organization?.planTier, active: organization?.active }}
          </DebugSection>
          <DebugSection title="Environment">
            {{ route: pathname, url: window.location.href, apiBase, version, userAgent: navigator.userAgent }}
          </DebugSection>
          <DebugSection title="Config (debug keys)">
            {{ "debug.mode": config["debug.mode"] }}
          </DebugSection>
          {apiErrors.length > 0 && (
            <DebugSection title="Recent API Errors">
              {apiErrors}
            </DebugSection>
          )}
          <DebugDiagnostics />
        </div>
      </details>
    </div>
  );
}
